import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {take, tap, throwError, Subscription} from 'rxjs';
import {ChatService} from 'src/app/home/services/chat.service';
import {MatDialog} from '@angular/material/dialog';
import {AlertDialogComponent} from 'src/app/shared/components/alert-dialog/alert-dialog.component';
import {DigoService} from 'src/app/services/digo.service';
import {FirebaseService} from 'src/app/services/firebase.service';
import {Timestamp} from 'firebase/firestore';
import {catchError, filter, finalize, switchMap} from 'rxjs/operators';
import {HttpErrorResponse} from '@angular/common/http';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { NgbModal, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { ChatHistoryModalComponent } from './chat-history-modal/chat-history-modal.component';
import {ChatCoordinatorService} from 'src/app/home/services/chat-coordinator.service';


@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.css'],
    standalone: false
})
export class ChatComponent implements OnInit, OnDestroy {
    @ViewChild('chatWidget') chatWidget!: ElementRef<HTMLDivElement>;
    @ViewChild('resizer') resizer!: ElementRef<HTMLDivElement>;

    public isOpen = false;
    public isMinimized = false;
    public chatId?: string | null;
    public pdv: any;
    public newMessage = '';
    public gpvName = '';
    public pdvId: any;
    public chatIdLoaded = false;
    public chatExists = false;
    public chatUnderConstruction = false;
    public showFloating = false;
    public showNewMessageToast = false;
    private toastTimer?: any;
    private unsubscribeChat?: () => void;
    private unsubscribeMessages?: () => void;
    private realtimeReady = false;
    public selectedFile: File | null = null;
    public unreadMessages: number = 0;
    private newMessageReceived = false;
    public messagesLoading = false;

    messages: Array<any> = [];

    private startX = 0;
    private startY = 0;
    private startWidth = 0;
    private startHeight = 0;
    private newWidth = 0;
    private newHeight = 0;

    private messageToDelete: string | null = null;
    private subscriptions: Subscription[] = [];

    archivedChats$ = this.chatService.archivedChats$;

    constructor(
        private firebaseService: FirebaseService,
        public chatService: ChatService,
        private chatCoordinator: ChatCoordinatorService,
        private digoService: DigoService,
        public dialog: MatDialog,
        private modalService: NgbModal,
        private config: NgbModalConfig
    ) {
        this.digoService.getPDV().pipe(
            filter(pdv => !!pdv),
            tap(pdv => {
                this.pdv = pdv;
                this.gpvName = pdv.gpv.name;
                this.pdvId = pdv.info.idpdv;
            }),
            tap(() => this.chatService.getArchivedChatsFromFilestore(this.pdvId)),
            switchMap(() => this.chatService.chats),
            finalize(() => {
                this.chatIdLoaded = true;
            })
        ).subscribe(() => {
            this.getChatId();
        });

        this.config.backdrop = 'static';
        this.config.keyboard = false;
        this.config.scrollable = true;
    }

    ngOnInit() {
        this.subscriptions.push(
            this.chatService.requestToggleChat$.subscribe(() => {
                if (this.chatUnderConstruction) {
                    return;
                }

                if (this.chatIdLoaded && !this.chatExists) {
                    this.createChat();
                    return;
                }

                this.toggle();
            }),
            this.chatCoordinator.closeChannel$.subscribe((channel) => {
                if (channel === 'dist') {
                    this.forceClose();
                }
            })
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        if (this.unsubscribeChat) {
            this.unsubscribeChat();
        }
        if (this.unsubscribeMessages) {
            this.unsubscribeMessages();
        }
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
    }

    initResize(event: MouseEvent) {
        event.preventDefault();

        if (!this.chatWidget) {
            return;
        }

        this.startX = event.clientX;
        this.startY = event.clientY;
        this.startWidth = this.chatWidget.nativeElement.offsetWidth;
        this.startHeight = this.chatWidget.nativeElement.offsetHeight;
        this.newWidth = this.startWidth;
        this.newHeight = this.startHeight;

        window.addEventListener('mousemove', this.resize);
        window.addEventListener('mouseup', this.stopResize);
    }

    resize = (event: MouseEvent) => {
        if (!this.chatWidget) {
            return;
        }

        // Diferencia desde la esquina superior izquierda
        const dx = this.startX - event.clientX;
        const dy = this.startY - event.clientY;

        // Nuevas dimensiones
        this.newWidth = this.startWidth + dx;
        this.newHeight = this.startHeight + dy;

        // Limites mínimos y máximos
        this.newWidth = Math.max(420, Math.min(700, this.newWidth));
        this.newHeight = Math.max(520, Math.min(1000, this.newHeight));

        this.chatWidget.nativeElement.style.width = this.newWidth + 'px';
        this.chatWidget.nativeElement.style.height = this.newHeight + 'px';
    };

    stopResize = () => {
        window.removeEventListener('mousemove', this.resize);
        window.removeEventListener('mouseup', this.stopResize);
    };

    public getChatId(): void {
        this.chatService.getPdvChat(this.pdvId)
            .pipe(
                take(1),
                tap((resp: any) => {
                    const id = resp?.msg?.id;
                    const byHierarchy = resp?.msg?.archived_by_hierarchy === true;

                    if (id != null && !byHierarchy) {
                        this.chatId = id;
                        this.chatExists = true;
                        this.chatIdLoaded = true;
                        this.chatService.setChatArchived(false);
                        // Iniciar escucha en tiempo real aunque el chat no esté abierto
                        this.startRealtimeMessages();
                    } else if (byHierarchy) {
                        this.chatExists = false;
                        this.chatIdLoaded = true;
                        this.chatService.setChatArchived(true);
                        this.startRealtimeNewChatListener();
                    }
                })
            )
            .subscribe({
                error: (error) => {
                    const backendMsg = error?.error?.error || error?.message || '';
                    this.chatExists = false;
                    this.chatIdLoaded = true;

                    console.error('Error al obtener el chat: ', error.status, error);

                    if (error.status == 409) {
                        this.startRealtimeNewChatListener();
                    }
                }
            });
    }

    private createChat() {
        this.chatUnderConstruction = true;
        this.chatService.createChatRemote(this.pdvId).pipe(
            tap((resp: any) => {
                this.chatId = resp.msg;
                this.chatExists = true;
                this.chatIdLoaded = true;
                this.chatService.setChatArchived(false);
                // Iniciar escucha en tiempo real aunque el chat no esté abierto
                this.startRealtimeMessages();
                this.toggle();
            }),
            catchError((error: HttpErrorResponse) => {
                this.chatExists = false;
                this.chatIdLoaded = true;

                return throwError(() => error);
            }),
            finalize(() => {
                this.chatUnderConstruction = false;
            })
        ).subscribe();
    }

    public toggle(): void {
        if (!this.chatIdLoaded) {
            this.dialog.open(AlertDialogComponent, {
                width: '420px',
                data: {message: 'Aún se está cargando los datos relacionados al chat.', className: ''}
            });
        } else if (!this.chatExists) {
            const message = `No se encontró el chat.<br/><br/><small>El chat para PDV "${this.pdv.info.name}" aún no existe.</small>`;
            this.dialog.open(AlertDialogComponent, {
                width: '420px',
                data: {message, className: ''}
            });
        } else {
            this.isOpen = !this.isOpen;
            this.chatService.openChat(this.isOpen);
            if (this.isOpen) {
                this.chatCoordinator.notifyOpened('dist');
                this.isMinimized = false;
                this.chatService.minimizedChat(this.isMinimized);
                this.hideNewMessageToast();
                this.startRealtimeMessages();
                this.hideNewMessageToast();
                this.scrollToBottom();
                try {
                    window.dispatchEvent(new CustomEvent('chat:opened'));
                } catch (e) {
                }

                setTimeout(() => {
                    this.resizer.nativeElement.addEventListener('mousedown', this.initResize.bind(this));

                    if (this.newWidth > 0 && this.newHeight > 0) {
                        this.chatWidget.nativeElement.style.width = this.newWidth + 'px';
                        this.chatWidget.nativeElement.style.height = this.newHeight + 'px';
                    }
                }, 100);
            } else {
                this.chatCoordinator.notifyClosed('dist');
            }
        }
    }

    public forceClose(): void {
        if (!this.isOpen) {
            return;
        }
        this.isOpen = false;
        this.isMinimized = false;
        this.chatService.openChat(false);
        this.chatService.minimizedChat(false);
        this.chatCoordinator.notifyClosed('dist');
        this.hideNewMessageToast();
    }

    public close(): void {
        if (this.isOpen) {
            this.forceClose();
        }
    }

    public toggleMinimize(): void {
        this.isMinimized = !this.isMinimized;
        this.chatService.minimizedChat(this.isMinimized);

        if (this.isMinimized) {
            this.chatWidget.nativeElement.removeAttribute('style');
        } else if (this.newWidth > 0 && this.newHeight > 0) {
            this.chatWidget.nativeElement.style.width = this.newWidth + 'px';
            this.chatWidget.nativeElement.style.height = this.newHeight + 'px';

            this.scrollToBottom();
        } else {
            this.scrollToBottom();
        }
    }

    public shouldShowDateSeparator(index: number): boolean {
        if (index === 0) {
            return true;
        }
        return this.messages[index - 1].date !== this.messages[index].date;
    }

    sendMessage() {
        const text = this.newMessage.trim();
        const file: File | null = this.selectedFile;

        if ((!text && !file) || !this.chatId) {
            return;
        }

        this.newMessage = '';
        this.selectedFile = null;

        let senderName = this.pdv.info.name;
        let senderType = 'pdv';
        let senderId = this.pdvId;

        let payload = {
            id: crypto.randomUUID(),
            chat_id: this.chatId,
            sender_id: senderId,
            sender_type: senderType,
            sender_name: senderName,
            message: text,
            distribuidor_read: senderType == 'distribuidor',
            gpv_read: senderType == 'gpv',
            pdv_read: false,
            file_status: file ? 'pending' : 'none',
            created_at: Timestamp.now()
        };

        this.chatService.createChatMessage(payload, file).then(() => {
            this.chatService.sendChatPushNotification(payload, file);

            if (file) {
                // Esperar a que termine la subida del archivo
                this.chatService.sendChatMessageFile(payload, file).subscribe({
                    next: () => {
                        // Hacer scroll después de que se complete la subida del archivo
                        setTimeout(() => {
                            this.scrollToBottom();
                        }, 500); // Pequeño delay para asegurar que la imagen se renderice
                    }
                });
            }
        });


    }

    private scrollToBottom(): void {
        setTimeout(() => {
            const chatScroll = document.querySelector('.messages-scroll') as HTMLElement | null;
            if (chatScroll) {
                chatScroll.scrollTop = chatScroll.scrollHeight;
            }

            this.chatService.markMessagesAsRead(`${this.chatId}`).then(() => {
                // Actualizar el conteo después de marcar como leídos
                this.unreadMessages = 0;
                this.chatService.updateUnreadCount(this.unreadMessages);
            });

        }, 100);
    }

    private startRealtimeMessages(): void {
        if (!this.chatId) {
            return;
        }
        if (this.unsubscribeChat) {
            this.unsubscribeChat();
        }
        if (this.unsubscribeMessages) {
            this.unsubscribeMessages();
        }
        this.messagesLoading = true;
        this.realtimeReady = false;
        this.unsubscribeChat = this.firebaseService.onChat(this.chatId, (chat: any) => {
            if (chat.archived == true) {
                //this.close();
                this.isOpen = false;
                this.chatId = null;
                this.chatExists = false;
                this.messages = [];
                this.messagesLoading = false;
                this.unsubscribeChat = undefined;

                if (chat.archived_by_hierarchy === true) {
                    this.chatIdLoaded = true;
                    this.chatService.setChatArchived(true);
                    this.startRealtimeNewChatListener();
                } else {
                    this.chatIdLoaded = false;
                    this.getChatId();
                }
            }
        });
        this.unsubscribeMessages = this.firebaseService.onMessages(this.chatId, (msgs) => {
            const toMillis = (ts: any): number => {
                if (!ts) {
                    return 0;
                }
                if (typeof ts === 'number') {
                    return ts;
                }
                if (ts.seconds != null) {
                    return (ts.seconds * 1000) + Math.floor((ts.nanoseconds || 0) / 1e6);
                }
                const d = new Date(ts);
                return isNaN(d.getTime()) ? 0 : d.getTime();
            };
            const formatTime = (ms: number) => new Date(ms).toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
            const formatDate = (ms: number) => new Date(ms).toLocaleDateString('es-ES');
            const mapSenderType = (senderType: string) => {
                const t = (senderType || '').toString().toLowerCase();
                if (t === 'pdv') {
                    return 'PDV';
                }
                if (t === 'gpv') {
                    return 'GPV';
                }
                if (t === 'user' || t === 'usuario') {
                    return 'USER';
                }
                return 'USER';
            };

            this.messages = msgs
                .sort((a: any, b: any) => toMillis(a.created_at) - toMillis(b.created_at))
                .map((m: any) => {
                    const createdMs = toMillis(m.created_at) || Date.now();
                    return {
                        id: m.id ?? m.message_id ?? createdMs,
                        date: formatDate(createdMs),
                        time: formatTime(createdMs),
                        senderType: mapSenderType(m.sender_type ?? ''),
                        senderId: m.sender_id,
                        senderName: m.sender_name ?? 'Usuario',
                        text: m.message ?? '',
                        file_url: m.file_url ?? '',
                        file_name: m.file_name,
                        file_status: m.file_status,
                        file_is_image: /\.(jpeg|jpg|gif|png|webp|bmp|svg)$/i.test(m.file_url),
                        read: m.pdv_read,
                        created_at: m.created_at.toDate(),
                    };
                });

            this.messagesLoading = false;

            const actualUndeadMessages = this.messages.filter(message => !message.read).length;

            this.newMessageReceived = actualUndeadMessages > 0 && actualUndeadMessages > this.unreadMessages;
            this.unreadMessages = actualUndeadMessages;

            // Notificar al servicio el conteo actualizado
            this.chatService.updateUnreadCount(this.unreadMessages);

            // Evitar toast en la primera carga del snapshot
            if (this.realtimeReady) {
                this.showToastTemporarily();
            } else {
                this.realtimeReady = true;
            }

            if (this.isOpen && !this.isMinimized) {
                this.scrollToBottom();
            }
        });
    }

    private startRealtimeNewChatListener() {
        this.chatService.onNewChat(this.pdvId, (chat: any) => {
            if (chat && chat.archived_by_hierarchy !== true) {
                console.log('Nuevo chat creado: ', chat);
                this.chatId = chat.id;
                this.chatExists = true;
                this.chatIdLoaded = true;
                this.chatService.setChatArchived(false);
                // Iniciar escucha en tiempo real aunque el chat no esté abierto
                this.startRealtimeMessages();
            }
        });
    }

    // Visual: mostrar aviso 7s y permitir abrir chat con botón "Ver"
    public showToastTemporarily(): void {
        if ((this.isOpen && !this.isMinimized) || !this.newMessageReceived) {
            return;
        }
        this.showNewMessageToast = true;
        try {
            window.dispatchEvent(new CustomEvent('chat:newMessage'));
        } catch (e) {
        }
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
        }
        this.toastTimer = setTimeout(() => {
            this.showNewMessageToast = false;
        }, 7000);
    }

    public hideNewMessageToast(ev?: Event): void {
        if (ev) {
            ev.stopPropagation();
        }
        this.showNewMessageToast = false;
        if (this.toastTimer) {
            clearTimeout(this.toastTimer);
            this.toastTimer = undefined;
        }
    }

    public onToastOpenClick(ev?: Event): void {
        if (ev) {
            ev.stopPropagation();
        }
        this.isOpen = false;
        this.hideNewMessageToast();
        this.toggle();
    }

    public openImageInNewTab(imageUrl: string): void {
        if (imageUrl) {
            window.open(imageUrl, '_blank');
        }
    }


    onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.selectedFile = input.files[0];
            //this.sendMessage();
        }
    }

    openFileSelector() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '*/*';
        input.onchange = (event: Event) => {
            this.onFileSelected(event);
        };
        input.click();
    }

    public fileIsImage(): boolean {
        return this.selectedFile?.type?.startsWith('image') ?? false;
    }

    public removeFile() {
        this.selectedFile = null;
    }

    public showDeleteMessage(message: any): boolean {
        return this.pdvId == message.senderId && (Date.now() - message.created_at) < 60 * 60 * 1000;
    }

    public requestDeleteMessage(message: any) {
        this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Eliminar mensaje',
                message: '¿Seguro que deseas eliminar este mensaje? Esta acción no se puede deshacer.'
            },
            panelClass: 'home'
        })
            .afterClosed()
            .subscribe((confirm: boolean) => {
                if (confirm) {
                    this.messageToDelete = message.id;
                    this.deleteMessage().then();
                }
            });
    }

    private async deleteMessage() {
        if (!this.chatId || !this.messageToDelete) {
            return;
        }

        await this.chatService.deleteMessage(this.chatId, this.messageToDelete);
    }

    openHistoryModal() {
        const modalRef = this.modalService.open(ChatHistoryModalComponent, {
            size: 'lg',
            centered: true,
            windowClass: 'chat-history-modal',
            scrollable: true,
            backdrop: 'static',
            keyboard: false
        });

        modalRef.componentInstance.chats$ = this.chatService.archivedChats$;
    }

}

